import { GRID_ROW_NUMBER_COL_DEF } from '@repo/go-table';
import { action, computed, makeObservable, observable } from 'mobx';

import {
  ACTIVITY_COLUMN_FIELD,
  CHECKBOX_SELECTION_COLUMN_FIELD,
  RECORD_BUTTONS_COLUMN,
} from 'src/components/directory-table/consts';
import {
  DEFAULT_TABLE_HEADER_ORIENTATION,
  type TableHeaderOrientation,
} from 'src/components/directory-table/header-orientation';
import { equalsDeep } from 'src/packages/utils/equals-deep';

import type {
  TableSettingsService,
  TTablePinModel,
  TTableSettingsObject,
  TTableSortItem,
} from './table-settings-service';
import type { TControlView } from '../directory-service/types';
import type { GridFilterModel } from '@repo/go-table';

import {
  checkAndFixFieldValues,
  checkAndFixFilterModel,
  checkAndFixOrderModel,
  checkAndFixPinModel,
  checkAndFixSortingModel,
  checkAndFixVisibilityModel,
  checkAndFixWidthModel,
} from './utils';

export type TSelectedCellData = { cellField: string; rowId: number };
export type TCellSelectionModel = Record<number, Record<string, boolean>>;

interface IDirectoryService {
  controlsViewsMap: Map<string, TControlView>;
  recordControlsViews: TControlView[];
}

export const HIDDEN_IN_SETTINGS_SIDEBAR_COLUMNS = [
  ACTIVITY_COLUMN_FIELD,
  CHECKBOX_SELECTION_COLUMN_FIELD,
  GRID_ROW_NUMBER_COL_DEF.field,
  RECORD_BUTTONS_COLUMN,
];

export class TableSettingsManager {
  private readonly directoryService: IDirectoryService;
  private tableSettingsService: TableSettingsService;
  private directoryName: string;

  @observable isSettingsOpen: boolean = false;
  @observable selectedCellData: TSelectedCellData | null = null;

  constructor(tableSettingsService: TableSettingsService, directoryName: string, directoryService: IDirectoryService) {
    this.tableSettingsService = tableSettingsService;
    this.directoryName = directoryName;
    this.directoryService = directoryService;

    makeObservable(this);
  }

  @computed
  get settings(): TTableSettingsObject {
    return this.tableSettingsService.tableSettings?.[this.directoryName] ?? {};
  }

  @computed
  get headerOrientation(): TableHeaderOrientation {
    return this.settings.headerOrientation ?? DEFAULT_TABLE_HEADER_ORIENTATION;
  }

  @computed
  get columnsOrderModel(): string[] {
    const tableColumnsFieldIds = this.directoryService.recordControlsViews.map((v) => v.fieldId);

    const columnsOrderModel = this.settings.columnsOrderModel;

    if (!columnsOrderModel?.length) {
      return tableColumnsFieldIds;
    }

    return tableColumnsFieldIds.sort((a, b) => {
      const c1index = columnsOrderModel.indexOf(a);
      const c2index = columnsOrderModel.indexOf(b);

      if (c1index < 0 && c2index < 0) {
        return 0;
      }

      if (c1index < 0) {
        return -1;
      }

      if (c2index < 0) {
        return 1;
      }

      return c1index - c2index;
    });
  }

  @computed
  get displaySettingPinnedColumns(): string[] {
    const excludeColumns = [ACTIVITY_COLUMN_FIELD, CHECKBOX_SELECTION_COLUMN_FIELD, GRID_ROW_NUMBER_COL_DEF.field];

    const leftPinnedColumns = this.settings.pinnedColumns?.left || [];
    const rightPinnedColumns = this.settings.pinnedColumns?.right || [];

    const includedLeftColumns = leftPinnedColumns.filter((column) => !excludeColumns.includes(column));

    return [...includedLeftColumns, ...rightPinnedColumns];
  }

  @action.bound
  checkSettings(controlViewsMap: Map<string, TControlView>): void {
    if (!this.settings) {
      return;
    }

    let haveSettingsBeenCorrected = false;
    let correctSettingsModel: TTableSettingsObject = { ...this.settings };

    const checkPinResult = checkAndFixPinModel(controlViewsMap, this.settings.pinnedColumns);
    if (checkPinResult.isCorrected) {
      correctSettingsModel = { ...correctSettingsModel, pinnedColumns: checkPinResult.model };

      haveSettingsBeenCorrected = true;
    }

    const checkWidthResult = checkAndFixWidthModel(controlViewsMap, this.settings.columnsWidth);
    if (checkWidthResult.isCorrected) {
      correctSettingsModel = { ...correctSettingsModel, columnsWidth: checkWidthResult.model };

      haveSettingsBeenCorrected = true;
    }

    const checkVisibilityResult = checkAndFixVisibilityModel(controlViewsMap, this.settings.columnsVisibility);
    if (checkVisibilityResult.isCorrected) {
      correctSettingsModel = { ...correctSettingsModel, columnsVisibility: checkVisibilityResult.model };

      haveSettingsBeenCorrected = true;
    }

    const checkOrderModelResult = checkAndFixOrderModel(controlViewsMap, this.settings.columnsOrderModel);
    if (checkOrderModelResult.isCorrected) {
      correctSettingsModel = { ...correctSettingsModel, columnsOrderModel: checkOrderModelResult.model };

      haveSettingsBeenCorrected = true;
    }

    const checkSortingModelResult = checkAndFixSortingModel(controlViewsMap, this.settings.sortingModel);
    if (checkSortingModelResult.isCorrected) {
      correctSettingsModel = { ...correctSettingsModel, sortingModel: checkSortingModelResult.model };

      haveSettingsBeenCorrected = true;
    }

    const checkFilteringModelResult = checkAndFixFilterModel(controlViewsMap, this.settings.filterModel);
    if (checkFilteringModelResult.isCorrected) {
      correctSettingsModel = { ...correctSettingsModel, filterModel: checkFilteringModelResult.model };

      haveSettingsBeenCorrected = true;
    }

    const checkFieldValuesResult = checkAndFixFieldValues(controlViewsMap, this.settings.fieldValues);
    if (checkFieldValuesResult.isCorrected) {
      correctSettingsModel = { ...correctSettingsModel, fieldValues: checkFieldValuesResult.model };

      haveSettingsBeenCorrected = true;
    }

    if (haveSettingsBeenCorrected) {
      this.tableSettingsService.updateSettings(this.directoryName, correctSettingsModel);
    }
  }

  @action.bound
  setSelectedCellData(data: TSelectedCellData | null): void {
    this.selectedCellData = data;
  }

  @action.bound
  setSettingsOpen(value: boolean): void {
    this.isSettingsOpen = value;
  }

  @action.bound
  onColumnsOrderModelChange(columns: string[]): void {
    if (equalsDeep(columns, this.settings.columnsOrderModel)) {
      return;
    }

    const newSettings = { ...this.settings, columnsOrderModel: columns };

    this.tableSettingsService.updateSettings(this.directoryName, newSettings);
  }

  @action.bound
  onSortChange(model: TTableSortItem[]): void {
    if (equalsDeep(model, this.settings.sortingModel)) {
      return;
    }

    const newSettings = { ...this.settings, sortingModel: model };

    this.tableSettingsService.updateSettings(this.directoryName, newSettings);
  }

  @action.bound
  onPinChange(pinnedColumns: TTablePinModel): void {
    if (
      equalsDeep(pinnedColumns, this.settings.pinnedColumns) ||
      (!!pinnedColumns.left && !pinnedColumns.right && !pinnedColumns.left.length && !this.settings.pinnedColumns)
    ) {
      return;
    }

    const newSettings = { ...this.settings, pinnedColumns };

    this.tableSettingsService.updateSettings(this.directoryName, newSettings);
  }

  @action.bound
  onWidthChange(incomeColumnsWidth: Record<string, number>): void {
    const newWidthSettings: Record<string, number> = { ...this.settings.columnsWidth, ...incomeColumnsWidth };

    this.tableSettingsService.updateSettings(this.directoryName, { ...this.settings, columnsWidth: newWidthSettings });
  }

  @action.bound
  onColumnVisibilityChange(model: Record<string, boolean>): void {
    if (equalsDeep(model, this.settings.columnsVisibility)) {
      return;
    }

    const newSettings = { ...this.settings, columnsVisibility: model };

    this.tableSettingsService.updateSettings(this.directoryName, newSettings);
  }

  @action.bound
  onFilterChange(model: GridFilterModel): void {
    if (equalsDeep(model, this.settings.filterModel)) {
      return;
    }

    const newSettings = { ...this.settings, filterModel: model };

    this.tableSettingsService.updateSettings(this.directoryName, newSettings);
  }

  @action.bound
  toggleHeaderOrientation(): void {
    this.tableSettingsService.updateSettings(this.directoryName, {
      ...this.settings,
      headerOrientation: this.headerOrientation === 'horizontal' ? 'vertical' : 'horizontal',
    });
  }

  resetSettings = (): void => {
    this.tableSettingsService.resetSettings(this.directoryName);
  };
}
